import React, { useEffect, useRef, useState } from 'react';
import ProfileModalSectionRender from './profileModalSectionRender';
import { useDispatch } from 'react-redux';
import { fetchAllOnboardingProfiles, submitHRTeamReview } from '../../../adminSlice';
import Loader from '../../../../components/loader';
import { isValidCostCenterId } from '../../../../features/utils';
import { ONBOARDING_FINAL_STEP } from '../../../../config';


function ProfileModalView({ sections, sectionRefs, pl='' }) {
    return (
        <>
            {sections.map((section, index) => (
                <div
                    key={section.key}
                    ref={sectionRefs[index]}
                    className="profile-review-section-shell inline-block h-full min-w-full align-top px-2 py-2 text-left sm:px-4"
                >
                    <ProfileModalSectionRender
                        ref={sectionRefs[index]}
                        name={section.key}
                        data={section.content}
                        pl={pl}
                    />
                </div>
            ))}
        </>
    );
}

const getStatusTag = (status) => {
    if (status === 'REJECTED') {
        return (
            <span className="inline-flex rounded-full bg-red-50 px-3 py-1 text-[11px] font-semibold tracking-wide text-red-700 ring-1 ring-inset ring-red-600/20">
                REJECTED
            </span>
        );
    } else if (status === 'APPROVED') {
        return (
            <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                APPROVED
            </span>
        );
    } else {
        return (
            <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold tracking-wide text-blue-700 ring-1 ring-inset ring-blue-600/20">
                PENDING
            </span>
        );
    }
};

export default function ProfileModal({ modalProfile, handleModalClose }) {
    const dispatch = useDispatch();
    const [isAgree, setIsAgree] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [data, setData] = useState({
        'notes': modalProfile?.hr_review?.notes || '',
        'cost_center_name': modalProfile?.hr_review?.cost_center_name || '',
        'cost_center_id': modalProfile?.hr_review?.cost_center_id || '',
    });

    const [costCenterError, setCostCenterError] = useState('');
    // Shown inside the panel once Approve / Reject is pressed, in place of alert()
    const [reviewMessage, setReviewMessage] = useState('');

    const handleDataChange = (e) => {
        const { name, value } = e.target;
        setData({ ...data, [name]: value });
        if (name === 'cost_center_id') {
            setCostCenterError(!value || isValidCostCenterId(value) ? '' : 'Please enter valid Cost Center ID');
        }
    }; 
    
    const handleSubmitReview = (status) => {
        // A profile that never started has no current_step; treat that as incomplete
        // (a plain `step / FINAL < 1` check is NaN there and would let it through).
        const currentStep = Number(modalProfile?.entry_point?.current_step);
        if (!Number.isFinite(currentStep) || currentStep < ONBOARDING_FINAL_STEP) {
            setReviewMessage("Review submission is not allowed until the profile is fully completed.");
        }
        else if (!isValidCostCenterId(data.cost_center_id)) {
            setCostCenterError('Please enter valid Cost Center ID');
            setReviewMessage('Please enter valid Cost Center ID');
        }
        else {
            setReviewMessage('');
            setIsSubmitting(true);
            dispatch(
                submitHRTeamReview({
                    status: status,
                    onboarding_profile: modalProfile?.profile?.id,
                    ...data,
                })
            )
            .unwrap()
            .then(() => {
                handleModalClose();
                dispatch(fetchAllOnboardingProfiles());
            })
            .finally(() => {
                setIsSubmitting(false);
            });
        }
    };

    const scrollContainerRef = useRef(null);

    const renderOrder = [
        'manager',
        'employees',
        'license_states',
        'processing_plan',
        'margin_management',
        'branch_location',
        'locking_cabinet',
        'local_it_company',
        'office_space',
        'mortgage_sign',
        'compliance_signage',
        'occupational_license',
        'marketing_details',
    ];

    // Extract all sections from the profile data
    const modalData = modalProfile?.profile?.data || {};
    const allSections = Object.keys(modalData);

    // Sort sections based on the renderOrder
    const sortedSections = renderOrder.filter((section) => allSections.includes(section));

    // Create sections array with keys and content
    const sections = sortedSections.map((key) => ({
        key,
        content: modalData[key],
    }));

    // Create refs for each section
    const sectionRefs = sections.map(() => React.createRef());

    useEffect(() => {
        // Disable background scrolling when modal is open
        document.body.style.overflow = 'hidden';

        // Re-enable scrolling when modal is closed
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []); // Empty dependency array ensures this runs on mount and unmount

    const handleScroll = (direction) => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const sectionElements = sectionRefs.map((ref) => ref.current);

            // Calculate cumulative scroll positions
            const sectionPositions = [];
            let cumulativeWidth = 0;

            sectionElements.forEach((section) => {
                sectionPositions.push(cumulativeWidth);
                cumulativeWidth += section.offsetWidth;
            });

            const containerScrollLeft = container.scrollLeft;

            // Determine current section index
            let currentSectionIndex = 0;

            for (let i = sectionPositions.length - 1; i >= 0; i--) {
                if (containerScrollLeft >= sectionPositions[i]) {
                    currentSectionIndex = i;
                    break;
                }
            }

            let targetIndex = currentSectionIndex;

            if (direction === 'left') {
                targetIndex = Math.max(0, currentSectionIndex - 1);
            } else if (direction === 'right') {
                targetIndex = Math.min(sections.length - 1, currentSectionIndex + 1);
            }

            // Scroll to the target section
            const targetScrollLeft = sectionPositions[targetIndex];
            container.scrollTo({
                left: targetScrollLeft,
                behavior: 'smooth',
            });

            // Reset vertical scroll of the target section
            const targetSectionRef = sectionRefs[targetIndex];
            if (targetSectionRef && targetSectionRef.current) {
                targetSectionRef.current.scrollTop = 0;
            }
        }
    };

    const actionsEnabled = isAgree && data.cost_center_id;

    return (
        <>
            {isSubmitting && <Loader />}
            <div
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                className="fixed inset-0 z-[60] flex h-full w-full items-center justify-center bg-[#211b4f]/45 p-3 backdrop-blur-sm sm:p-6"
            >
                <div className="relative flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                    <header className="sticky top-0 z-20 flex items-center justify-end gap-4 border-b border-slate-200 bg-white/95 px-4 py-3.5 backdrop-blur sm:px-5">
                        {modalProfile?.hr_review?.status && getStatusTag(modalProfile?.hr_review?.status)}
                        <button
                            type="button"
                            className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-[#211b4f] focus:outline-none focus:ring-2 focus:ring-[#fb6635]"
                            onClick={handleModalClose}
                            aria-label="Close modal"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 14 14" aria-hidden="true">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7L1 13" />
                            </svg>
                        </button>
                    </header>

                    <div className="relative flex min-h-0 flex-1 items-center">
                        <button
                            type="button"
                            onClick={() => handleScroll('left')}
                            className="absolute left-2 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-md transition-colors hover:bg-slate-100 hover:text-[#211b4f]"
                            aria-label="Previous section"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <div className="h-full w-full min-w-0 flex-1 overflow-x-auto whitespace-nowrap scroll-smooth scrollbar-hide" ref={scrollContainerRef}>
                            <ProfileModalView sections={sections} sectionRefs={sectionRefs} pl={modalProfile?.profile?.data?.pl} />
                        </div>
                        <button
                            type="button"
                            onClick={() => handleScroll('right')}
                            className="absolute right-6 top-1/2 z-10 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-md transition-colors hover:bg-slate-100 hover:text-[#211b4f]"
                            aria-label="Next section"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>

                    <section className="flex-none border-t border-slate-200 bg-[#f7f2ef] p-4">
                        <label htmlFor="activateActions" className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm transition-colors hover:border-orange-200">
                                <input
                                    type="checkbox"
                                    id="activateActions"
                                    checked={isAgree}
                                    onChange={(e) => setIsAgree(e.target.checked)}
                                    className="h-5 w-5 rounded border-slate-300 text-[#fb6635] focus:ring-orange-500"
                                />
                                <span className="text-sm font-medium text-[#211b4f]">I have verified the information on all screens.</span>
                        </label>

                        <label className="mt-3 block">
                            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">Notes</span>
                            <textarea
                                id="message"
                                rows="2"
                                name="notes"
                                value={data.notes}
                                onChange={handleDataChange}
                                className="block w-full resize-y rounded-xl border-slate-300 bg-white p-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-orange-400 focus:ring-orange-400"
                                placeholder="Write your notes here..."
                            />
                        </label>

                        {/* Cost center name + ID share one row, same label / input styling. */}
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <label className="block">
                                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">Cost Center Name</span>
                                <input
                                    type="text"
                                    name="cost_center_name"
                                    id="cost_center_name"
                                    value={data.cost_center_name}
                                    onChange={handleDataChange}
                                    className="h-[46px] w-full rounded-xl border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-orange-400 focus:ring-orange-400"
                                    placeholder="Cost Center Name"
                                />
                            </label>
                            <label className="block">
                                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">Cost Center ID <span className="text-red-600">*</span></span>
                                <input
                                    type="text"
                                    name="cost_center_id"
                                    id="cost_center_id"
                                    value={data.cost_center_id}
                                    onChange={handleDataChange}
                                    className={`h-[46px] w-full rounded-xl bg-white px-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:ring-orange-400 ${costCenterError ? 'border-red-400 focus:border-red-400' : 'border-slate-300 focus:border-orange-400'}`}
                                    placeholder="Cost Center ID"
                                    required
                                />
                                {costCenterError && <span className="mt-1 block text-xs font-medium text-red-600">{costCenterError}</span>}
                            </label>
                        </div>

                        {reviewMessage && (
                            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                <svg className="h-5 w-5 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <span>{reviewMessage}</span>
                            </div>
                        )}

                        <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                    <button
                                        type="button"
                                        onClick={() => handleSubmitReview('REJECTED')}
                                        disabled={!actionsEnabled}
                                        className={`inline-flex h-11 items-center justify-center rounded-xl border px-6 text-sm font-semibold transition-colors ${actionsEnabled ? 'border-red-200 bg-white text-red-700 hover:bg-red-50' : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'}`}
                                    >
                                        Reject
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleSubmitReview('APPROVED')}
                                        disabled={!actionsEnabled}
                                        className={`inline-flex h-11 items-center justify-center gap-2 rounded-xl px-6 text-sm font-semibold text-white shadow-sm transition-colors ${actionsEnabled ? 'bg-[#211b4f] hover:bg-[#30266c]' : 'cursor-not-allowed bg-slate-400'}`}
                                    >
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m5 12 4 4L19 6" />
                                        </svg>
                                        Approve
                                    </button>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
}
